import { useCallback, useRef, useState } from 'react'
import { fetchGttsFrenchAudio } from '../api/apiConfig'
import type { StartRecordingOptions, VoiceStreamStatus } from './useVoiceStreaming'

const TRAILING_SILENCE_MS = 2200

type Args = {
  apiBaseUrl: string
  micStatus: VoiceStreamStatus
  onStartMic: (options?: StartRecordingOptions) => Promise<void>
  onStopMic: () => void
}

function waitForMicStatus(
  readStatus: () => VoiceStreamStatus,
  targets: VoiceStreamStatus[],
  timeoutMs: number,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const started = Date.now()
    const tick = () => {
      if (targets.includes(readStatus())) {
        resolve()
        return
      }
      if (Date.now() - started > timeoutMs) {
        reject(new Error('Microphone session timed out'))
        return
      }
      window.setTimeout(tick, 50)
    }
    tick()
  })
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms))
}

/** Temporary: gTTS plays typed text while the existing mic/STT session listens. */
export function useGttsSpeakAndTranscribe(args: Args) {
  const { apiBaseUrl, micStatus, onStartMic, onStopMic } = args
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const statusRef = useRef(micStatus)
  statusRef.current = micStatus

  const speakAndTranscribe = useCallback(
    async (text: string) => {
      const phrase = text.trim()
      if (!phrase || busy) return

      const micSessionActive =
        statusRef.current === 'requesting_permission' ||
        statusRef.current === 'connecting' ||
        statusRef.current === 'recording' ||
        statusRef.current === 'transcribing'

      setBusy(true)
      setError(null)

      let audio: HTMLAudioElement | null = null
      let objectUrl: string | null = null

      try {
        const blobPromise = fetchGttsFrenchAudio(apiBaseUrl, phrase)

        if (!micSessionActive) {
          await Promise.all([
            onStartMic({ speakerPlaybackCapture: true }).then(() =>
              waitForMicStatus(() => statusRef.current, ['recording'], 20000),
            ),
            blobPromise,
          ])
        }

        const blob = await blobPromise
        objectUrl = URL.createObjectURL(blob)
        audio = new Audio(objectUrl)

        await new Promise<void>((resolve, reject) => {
          if (!audio) {
            reject(new Error('Audio element missing'))
            return
          }
          audio.onended = () => resolve()
          audio.onerror = () => reject(new Error('gTTS playback failed'))
          void audio.play().catch(reject)
        })

        await sleep(TRAILING_SILENCE_MS)

        if (statusRef.current === 'recording') {
          onStopMic()
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err)
        setError(message)
      } finally {
        if (audio) {
          audio.pause()
          audio.src = ''
        }
        if (objectUrl) {
          URL.revokeObjectURL(objectUrl)
        }
        setBusy(false)
      }
    },
    [apiBaseUrl, busy, onStartMic, onStopMic],
  )

  return { gttsBusy: busy, gttsError: error, speakAndTranscribe, clearGttsError: () => setError(null) }
}
